<!-- eslint-disable vuejs-accessibility/anchor-has-content -->
<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useHead } from '@vueuse/head';
import { useBlog } from '@/composables/useBlog';
import { formatFirebaseTimestamp } from '@/helpers/index';
import { incrementViewCount } from '@/service/blog.service';
import { useLikes } from '@/composables/useBlogLikes';
import BlogItem from '@/components/BlogItem.vue';
import usePopularBlogs from '@/composables/usePopularBlogs';
import NavBar from '@/components/NavBar.vue';
import Footer from '@/components/Footer.vue';

const route = useRoute();

const { blog, getBlogBySlug, isLoading } = useBlog();
const { initializeLikes, isLiked, toggleLike } = useLikes();
const {
  data: popularBlogs,
  isLoading: popularIsLoading,
  isError,
  refetch,
} = usePopularBlogs(blog);

let viewTimeout: ReturnType<typeof setTimeout> | null = null;
const incrementViewWithDelay = (blogId: string) => {
  if (viewTimeout) {
    clearTimeout(viewTimeout);
  }

  viewTimeout = setTimeout(() => {
    incrementViewCount(blogId);
  }, 2000);
};

onMounted(async () => {
  const { slug } = route.params;
  await getBlogBySlug(slug as string);

  if (blog.value) {
    incrementViewWithDelay(blog.value.id);
    initializeLikes();
    refetch();
    useHead({
      title: `${blog.value.title} | TALAB - Ultimate Student Concierge App!`,
      meta: [
        {
          name: 'description',
          content:
            blog.value.metaDescription
            || 'Discover TALAB – the ultimate app for students in London! Find everything you need, from dry cleaning and apartment rentals to chauffeur services and event updates. Enjoy personalized assistance and seamless access to all essential services in one app. Download TALAB today!',
        },
        {
          property: 'og:title',
          content: blog.value.title,
        },
        {
          property: 'og:description',
          content:
            blog.value.metaDescription
            || 'Discover TALAB – the ultimate app for students in London! Find everything you need, from dry cleaning and apartment rentals to chauffeur services and event updates. Enjoy personalized assistance and seamless access to all essential services in one app. Download TALAB today!',
        },
        {
          property: 'og:url',
          content: `https:/talabapp.co.uk/blog/${slug}`,
        },
        {
          property: 'og:type',
          content: 'article',
        },
      ],
      link: [
        {
          rel: 'canonical',
          href: `https:/talabapp.co.uk/blog/${slug}`,
        },
      ],
    });
  }
});

const handleLikeToggle = async (blogId: string) => {
  if (!blog.value) return;

  const currentLikes = blog.value.likes ?? 0;
  const postLiked = isLiked(blogId);

  blog.value.likes = postLiked ? currentLikes - 1 : currentLikes + 1;

  await toggleLike(blogId, currentLikes);
};
</script>

<template>
  <NavBar />
  <section
    class="relative pt-32 pb-20 bg-[#151515] border-t-2 border-[#7F7F7F]"
  >
    <Transition>
      <div
        class="w-full max-w-lg md:max-w-2xl lg:max-w-4xl px-5 lg:px-11 mx-auto max-md:px-4"
        v-if="blog"
      >
        <h1
          class="text-white font-manrope font-semibold text-4xl min-[500px]:text-5xl leading-tight mb-2 font-josefin"
        >
          {{ blog?.title ?? "Loading..." }}
        </h1>
        <div class="flex items-center justify-between">
          <p class="text-base text-[#7F7F7F] leading-8 mb-1">
            {{ formatFirebaseTimestamp(blog?.createdAt) }}
          </p>
          <div class="flex items-center gap-5">
            <a
              href="https://www.instagram.com/talab_uk/"
              target="_blank"
              class="hover:scale-125 transition-all"
            >
              <svg
                width="40"
                height="40"
                viewBox="0 0 40 40"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M15.4234 19.8791C15.4234 17.3223 17.526 15.2491 20.1203 15.2491C22.7146 15.2491 24.8183 17.3223 24.8183 19.8791C24.8183 22.4359 22.7146 24.5091 20.1203 24.5091C17.526 24.5091 15.4234 22.4359 15.4234 19.8791ZM12.8837 19.8791C12.8837 23.818 16.1235 27.0109 20.1203 27.0109C24.1171 27.0109 27.3569 23.818 27.3569 19.8791C27.3569 15.9402 24.1171 12.7473 20.1203 12.7473C16.1235 12.7473 12.8837 15.9402 12.8837 19.8791ZM25.9522 12.4646C25.9521 12.7942 26.0511 13.1165 26.2368 13.3906C26.4225 13.6648 26.6866 13.8785 26.9955 14.0048C27.3045 14.131 27.6445 14.1642 27.9726 14.1C28.3007 14.0358 28.6021 13.8772 28.8387 13.6442C29.0753 13.4112 29.2365 13.1143 29.3019 12.791C29.3673 12.4678 29.3339 12.1326 29.206 11.828C29.0781 11.5235 28.8615 11.2631 28.5835 11.0798C28.3054 10.8966 27.9785 10.7987 27.644 10.7986H27.6433C27.195 10.7988 26.7651 10.9743 26.448 11.2867C26.1309 11.5991 25.9526 12.0227 25.9522 12.4646ZM14.4267 31.1843C13.0527 31.1227 12.3059 30.8971 11.8096 30.7066C11.1517 30.4541 10.6822 30.1535 10.1886 29.6677C9.69501 29.1819 9.38947 28.7197 9.13445 28.0712C8.94098 27.5823 8.71211 26.8461 8.64965 25.492C8.58133 24.028 8.56768 23.5882 8.56768 19.8792C8.56768 16.1702 8.58245 15.7317 8.64965 14.2665C8.71222 12.9123 8.94278 12.1776 9.13445 11.6872C9.3906 11.0388 9.69568 10.5761 10.1886 10.0897C10.6815 9.60323 11.1505 9.30212 11.8096 9.05079C12.3057 8.86012 13.0527 8.63457 14.4267 8.57301C15.9123 8.50568 16.3585 8.49223 20.1203 8.49223C23.8821 8.49223 24.3288 8.50679 25.8155 8.57301C27.1896 8.63468 27.9351 8.8619 28.4327 9.05079C29.0906 9.30212 29.5601 9.6039 30.0537 10.0897C30.5473 10.5755 30.8517 11.0388 31.1078 11.6872C31.3013 12.1761 31.5302 12.9123 31.5926 14.2665C31.661 15.7317 31.6746 16.1702 31.6746 19.8792C31.6746 23.5882 31.661 24.0268 31.5926 25.492C31.5301 26.8461 31.3001 27.5821 31.1078 28.0712C30.8517 28.7197 30.5466 29.1823 30.0537 29.6677C29.5608 30.153 29.0906 30.4541 28.4327 30.7066C27.9366 30.8972 27.1896 31.1228 25.8155 31.1843C24.33 31.2517 23.8838 31.2651 20.1203 31.2651C16.3568 31.2651 15.9118 31.2517 14.4267 31.1843ZM14.3101 6.07435C12.8098 6.14168 11.7846 6.37612 10.8893 6.71946C9.9621 7.07401 9.17718 7.54968 8.39282 8.32146C7.60846 9.09323 7.12705 9.86801 6.76728 10.7818C6.4189 11.6647 6.18101 12.6745 6.11269 14.153C6.04324 15.6339 6.02734 16.1073 6.02734 19.8791C6.02734 23.6509 6.04324 24.1243 6.11269 25.6052C6.18101 27.0839 6.4189 28.0936 6.76728 28.9765C7.12705 29.8897 7.60857 30.6653 8.39282 31.4368C9.17706 32.2082 9.9621 32.6832 10.8893 33.0388C11.7863 33.3821 12.8098 33.6166 14.3101 33.6839C15.8135 33.7512 16.2931 33.768 20.1203 33.768C23.9475 33.768 24.4279 33.7523 25.9305 33.6839C27.4309 33.6166 28.4554 33.3821 29.3513 33.0388C30.2779 32.6832 31.0634 32.2086 31.8478 31.4368C32.6321 30.665 33.1125 29.8897 33.4733 28.9765C33.8217 28.0936 34.0607 27.0838 34.1279 25.6052C34.1962 24.1232 34.2121 23.6509 34.2121 19.8791C34.2121 16.1073 34.1962 15.6339 34.1279 14.153C34.0596 12.6743 33.8217 11.6641 33.4733 10.7818C33.1125 9.86857 32.6309 9.09446 31.8478 8.32146C31.0647 7.54846 30.2779 7.07401 29.3524 6.71946C28.4554 6.37612 27.4308 6.14057 25.9317 6.07435C24.429 6.00701 23.9486 5.99023 20.1214 5.99023C16.2942 5.99023 15.8135 6.0059 14.3101 6.07435Z"
                  fill="#BC8809"
                />
              </svg>
            </a>
            <a
              href="https://www.tiktok.com/@talabuk"
              target="_blank"
              class="hover:scale-125 transition-all"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                shape-rendering="geometricPrecision"
                text-rendering="geometricPrecision"
                image-rendering="optimizeQuality"
                fill-rule="evenodd"
                clip-rule="evenodd"
                viewBox="0 0 449.45 515.38"
                width="27"
                height="27"
              >
                <path
                  fill-rule="nonzero"
                  d="M382.31 103.3c-27.76-18.1-47.79-47.07-54.04-80.82-1.35-7.29-2.1-14.8-2.1-22.48h-88.6l-.15 355.09c-1.48 39.77-34.21 71.68-74.33 71.68-12.47 0-24.21-3.11-34.55-8.56-23.71-12.47-39.94-37.32-39.94-65.91 0-41.07 33.42-74.49 74.48-74.49 7.67 0 15.02 1.27 21.97 3.44V190.8c-7.2-.99-14.51-1.59-21.97-1.59C73.16 189.21 0 262.36 0 352.3c0 55.17 27.56 104 69.63 133.52 26.48 18.61 58.71 29.56 93.46 29.56 89.93 0 163.08-73.16 163.08-163.08V172.23c34.75 24.94 77.33 39.64 123.28 39.64v-88.61c-24.75 0-47.8-7.35-67.14-19.96z"
                  fill="#BC8809"
                />
              </svg>
            </a>
          </div>
        </div>
        <div class="flex items-center gap-5 mt-5">
          <div class="flex items-center gap-1">
            <i class="pi pi-eye text-white"></i>
            <p class="text-white text-sm">{{ blog.views ?? 0 }}</p>
          </div>
          <div
            class="flex items-center gap-1 cursor-pointer"
            @click="handleLikeToggle(blog.id)"
          >
            <i
              :class="
                isLiked(blog.id)
                  ? 'pi pi-heart-fill text-red-500'
                  : 'pi pi-heart text-white'
              "
              class="hover:scale-125 transition-all"
            ></i>
            <p class="text-white text-sm">{{ blog.likes ?? 0 }}</p>
          </div>
          <!-- <div class="flex items-center gap-1">
          <i class="pi pi-comment text-white"></i>
          <p class="text-white text-sm">18</p>
        </div> -->
        </div>
      </div>
    </Transition>
  </section>
  <div v-if="isLoading" class="h-[100vh]"></div>
  <Transition>
    <section class="relative py-10 lg:py-16" v-if="blog">
      <div
        class="w-full max-w-lg md:max-w-2xl lg:max-w-4xl px-5 lg:px-11 mx-auto max-md:px-3 content"
        v-html="blog?.content"
      ></div>

      <div
        class="mt-28 w-full max-w-lg md:max-w-2xl lg:max-w-4xl px-5 lg:px-11 mx-auto max-md:px-3"
      >
        <h2 class="text-4xl font-bold mb-14 text-center font-josefin">
          Our Popular Blogs
        </h2>
        <div v-if="popularIsLoading">Loading...</div>
        <div v-else-if="isError">Error loading popular blogs</div>
        <div v-else class="grid grid-cols-3 gap-2">
          <BlogItem
            v-for="post in popularBlogs"
            :key="post.id"
            :post="post"
            class="w-full"
          />
        </div>
        <a
          class="border border-[#d8cec6] shadow-sm rounded-full py-3.5 px-7 w-52 flex justify-center items-center text-[#151515] font-semibold mx-auto transition-all duration-300 hover:bg-[#BC8809] hover:text-white mt-10 cursor-pointer"
          href="/blog"
        >
          <p class="text-lg">View All</p>
        </a>
      </div>

      <!-- <div
      class="comments w-full max-w-lg md:max-w-2xl lg:max-w-4xl px-5 lg:px-11 mx-auto max-md:px-3 py-10 mt-20 bg-white rounded-xl font-crimson"
    >
      <h5 class="font-medium text-xl leading-8 text-[#151515] mb-8">
        Comments
      </h5>
      <div class="flex flex-col sm:flex-row gap-x-9 gap-y-4 w-full mb-7">
        <img
          src="@/assets/img/user-icon.webp"
          alt="William image"
          class="h-10 w-10 object-cover"
        />
        <div class="data w-full">
          <h6 class="font-medium text-xl leading-8 text-black">
            William Smith
          </h6>
          <p class="font-normal text-base leading-7 text-[#151515]">
            I have been using pagedone for several months now, and I must say
            that it has made my life a lot easier. The platform's intuitive
            interface and ease of use have allowed me to manage my finances more
            effectively and make informed investment decisions.
          </p>
        </div>
      </div>
      <div class="flex flex-col sm:flex-row gap-x-9 gap-y-4 w-full mb-16">
        <img
          src="@/assets/img/user-icon.webp"
          alt="Ankita image"
          class="h-10 w-10 object-cover"
        />
        <div class="data w-full">
          <h6 class="font-medium text-xl leading-8 text-black">Ankita Jha</h6>
          <p class="font-normal text-base leading-7 text-[#151515]">
            I have been using pagedone for several months now, and I must say
            that it has made my life a lot easier. The platform's intuitive
            interface and ease of use have allowed me to manage my finances more
            effectively and make informed investment decisions.
          </p>
        </div>
      </div>
      <h5 class="font-medium text-xl leading-8 text-[#151515] mb-6">
        Leave a comment
      </h5>
      <div
        class="reply bg-gray-100 rounded-xl p-5 border border-solid border-gray-300 w-full"
      >
        <div
          class="flex items-center gap-x-9 gap-y-4 flex-col sm:flex-row mb-5"
        >
          <textarea
            class="font-medium text-xl leading-8 placeholder:text-[#151515] text-[#151515] bg-transparent outline-0 w-full h-28"
            placeholder="Type a comment here"
          ></textarea>
        </div>
        <button
          class="rounded-full py-3 px-6 bg-indigo-600 shadow-md font-semibold text-sm text-white transition-all duration-500 hover:bg-indigo-700 hover:shadow-indigo-200"
        >
          Post
        </button>
      </div>
    </div> -->
    </section>
  </Transition>

  <Footer />
  <!-- <LoadingScreen :display="isLoading" /> -->
</template>

<style lang="scss">
.content {
  h1 {
    @apply text-4xl;
    @apply mb-2;
  }
  h2 {
    @apply text-3xl;
    @apply mb-2;
  }
  p {
    @apply mb-5;
  }
  blockquote {
    @apply border-l-2;
    @apply border-l-[#A58E74];
    font-style: italic;
    padding-left: 1rem;
  }
  a {
    @apply underline;
    @apply hover:opacity-40;
  }
  ul {
    list-style-type: disc;
    margin-left: 2rem; /* Customize indentation */
  }

  ol {
    list-style-type: decimal;
    margin-left: 2rem;
  }

  li {
    margin-bottom: 0.5rem; /* Customize spacing between list items */
    @apply text-base;
  }

  li::marker {
    color: #7f7f7f; /* Customize the bullet or number color */
  }
}
</style>
